import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Interceptor para agregar el token y la sucursal activa
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const sucursalId = localStorage.getItem('sucursal_id');
  if (sucursalId) {
    config.headers['X-Sucursal-Id'] = sucursalId;
  }
  return config;
});

// Interceptor para errores 401 y 403
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Token inválido o expirado → limpiar y redirigir al login
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }

    // Cuenta suspendida → Muro de Peaje (suspensión administrativa)
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === 'ACCOUNT_SUSPENDED'
    ) {
      localStorage.clear();
      window.location.href = '/expired?reason=suspended';
    }

    // Trial expirado → Muro de Peaje (período de prueba terminado)
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === 'TRIAL_EXPIRED'
    ) {
      localStorage.clear();
      window.location.href = '/expired?reason=trial';
    }

    return Promise.reject(error);
  }
);


export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/profile'),
  changePassword: (data) => api.put('/auth/change-password', data),
};

export const productsAPI = {
  getAll: (status = 'active', sucursalId = null) => api.get(`/products?status=${status}${sucursalId ? `&sucursal_id=${sucursalId}` : ''}`),
  create: (product) => api.post('/products', product),
  update: (id, product) => api.put(`/products/${id}`, product),
  delete: (id) => api.delete(`/products/${id}`),
  search: (term, sucursalId = null) => api.get(`/products/search?term=${term}${sucursalId ? `&sucursal_id=${sucursalId}` : ''}`),
  restore: (id) => api.put(`/products/${id}/restore`),
  permanentDelete: (id) => api.delete(`/products/${id}/permanent`),
  getStats: (sucursalId = null) => api.get(`/products/stats${sucursalId ? `?sucursal_id=${sucursalId}` : ''}`),
  getLowStock: (sucursalId = null) => api.get(`/products/low-stock${sucursalId ? `?sucursal_id=${sucursalId}` : ''}`),
  getCategories: () => api.get('/products/categories'),
  importBulk: (products, sucursalId = null) => api.post('/products/import-bulk', { products, sucursal_id: sucursalId }),
};

export const customersAPI = {
  getAll: () => api.get('/customers'),
  getDebtors: () => api.get('/customers/debtors'),
  create: (customer) => api.post('/customers', customer),
  update: (id, customer) => api.put(`/customers/${id}`, customer),
  delete: (id) => api.delete(`/customers/${id}`),
  search: (term) => api.get(`/customers/search?term=${term}`),
  updateCreditLimit: (id, limit) => api.patch(`/customers/${id}/credit-limit`, { credit_limit: limit }),
};

// ✅ CORRECCIÓN: Agregado createOrder para compatibilidad con el Modal
export const salesAPI = {
  // --- Rutas Generales ---
  getAll: (params) => api.get('/sales', { params }),
  
  create: (sale) => api.post('/sales', sale), // Sirve para Venta Directa
  
  // 👇 ESTA ES LA LÍNEA QUE FALTABA 👇
  createOrder: (data) => api.post('/sales', data), 
  
  getById: (id) => api.get(`/sales/${id}`),
  getRecent: () => api.get('/sales/recent'),
  
  // --- Rutas de Gestión de Pedidos (OrdersManagementPage) ---
  getOrders: (params) => api.get('/sales/orders', { params }),
  
  getPendingOrdersCount: () => api.get('/sales/stats/orders'),
  
  addPayment: (id, data) => api.post(`/sales/${id}/payment`, data),
  
  updateStatus: (id, data) => api.patch(`/sales/${id}/status`, data),
  
  invoiceOrder: (id, data) => api.post(`/sales/${id}/invoice`, data),
  
  deleteOrder: (id) => api.delete(`/sales/${id}`),

  // 🚫 Anular venta entregada directo (Dueño, 1 paso — restaura stock, no elimina)
  voidSale: (id) => api.put(`/sales/${id}/void`),

  // ── Flujo de anulación en 2 pasos (Encargada solicita → Dueño aprueba/rechaza) ──
  solicitarAnulacion: (id, data) => api.post(`/sales/${id}/solicitar-anulacion`, data),
  aprobarAnulacion:   (id)       => api.post(`/sales/${id}/aprobar-anulacion`),
  rechazarAnulacion:  (id, data) => api.post(`/sales/${id}/rechazar-anulacion`, data),

  // 📡 Sincronizar batch de ventas offline al recuperar internet
  // Recibe array de ventas, devuelve { synced, failed, results, warnings }
  syncOffline: (sales) => api.post('/sales/sync-offline', { sales }),

};

export const inventoryAPI = {
  getMovements: (filters) => api.get('/inventory/movements', { params: filters }),
  createMovement: (movementData) => api.post('/inventory/movements', movementData),
  getLotes: (params) => api.get('/inventory/lotes', { params }),
  getAlertasLotes: (params) => api.get('/inventory/lotes/alertas', { params }),
  createLote: (loteData) => api.post('/inventory/lotes', loteData),
  adjustLote: (id, data) => api.put(`/inventory/lotes/${id}/adjust`, data),
  updateLoteInfo: (id, data) => api.put(`/inventory/lotes/${id}/info`, data),
};

export const dashboardAPI = {
  getStats: (params) => api.get('/dashboard/stats', { params }),
  getDashboardData: (period = 30) => api.get(`/reports/dashboard?period=${period}`),
};

export const reportsAPI = {
    getDashboard: (params) => api.get('/reports/dashboard', { params }),
    getSalesReport: (params) => api.get('/reports/sales', { params }),
    getTopProductsReport: (params) => api.get('/reports/top-products', { params }),
    getCustomerReport: (params) => api.get('/reports/customers', { params }),
    getInventoryReport: (params) => api.get('/reports/inventory', { params }),
    getProfitabilityReport: (params) => api.get('/reports/profitability', { params }),
    getTrendsAnalysis: (params) => api.get('/reports/trends', { params }),
    getFinancialReport: (params) => api.get('/reports/financial', { params }),
    getPeriodComparison: (params) => api.get('/reports/comparison', { params }),
    getExpenseReport: (params) => api.get('/reports/expenses', { params })
};

export const calendarAPI = {
  getEvents: (params) => api.get('/calendar/events', { params }),
  getDeliverySummary: (params) => api.get('/calendar/delivery-summary', { params }),
  updateDeliveryDate: (orderId, data) => api.patch(`/calendar/orders/${orderId}/delivery-date`, data),
  getStats: (params) => api.get('/calendar/stats', { params }),
};
export const expensesAPI = {
  getAll:  (params) => api.get('/expenses', { params }),
  create:  (expense) => api.post('/expenses', expense),
  delete:  (id) => api.delete(`/expenses/${id}`),
};

// 🛡️ SuperAdmin API
export const adminAPI = {
  getTenants:          ()              => api.get('/admin/tenants'),
  createTenant:        (data)          => api.post('/admin/tenants', data),
  updateTenantStatus:  (id, status)    => api.patch(`/admin/tenants/${id}/status`, { status }),
  updateTenant:        (id, data)      => api.patch(`/admin/tenants/${id}`, data),
  resetPassword:       (id, password)  => api.patch(`/admin/tenants/${id}/reset-password`, { new_password: password }),
};

// 🏪 Sucursales API — Multi-Sucursal
export const sucursalesAPI = {
  getAll:           ()           => api.get('/sucursales'),
  getById:          (id)         => api.get(`/sucursales/${id}`),
  create:           (data)       => api.post('/sucursales', data),
  update:           (id, data)   => api.put(`/sucursales/${id}`, data),
  getInventario:    (id)         => api.get(`/sucursales/${id}/inventario`),
  ajustarInventario:(id, data)   => api.post(`/sucursales/${id}/inventario/ajuste`, data),
  regenerarCodigo:  (id)         => api.patch(`/sucursales/${id}/codigo-acceso/regenerar`),

  // 💰 Precio especial por sucursal (sobreescritura opcional del precio general)
  getProductos:      (id)                    => api.get(`/sucursales/${id}/productos`),
  setPrecioEspecial: (id, productoId, data)  => api.put(`/sucursales/${id}/productos/${productoId}/precio-especial`, data),
};

// 🔓🔒 Caja POR SUCURSAL — Abrir/Cerrar
export const businessAPI = {
  getStatus: (sucursalId) => api.get(`/business/${sucursalId}/status`),
  open:      (sucursalId) => api.post(`/business/${sucursalId}/open`),
  close:     (sucursalId) => api.post(`/business/${sucursalId}/close`),
};

// 👷 Empleados API — Cajeros por Sucursal
export const empleadosAPI = {
  getAll:      ()           => api.get('/empleados'),
  create:      (data)       => api.post('/empleados', data),
  update:      (id, data)   => api.put(`/empleados/${id}`, data),
  deactivate:  (id)         => api.delete(`/empleados/${id}`),
  // Login de cajero — NO necesita token previo (ruta pública)
  cajeroLogin: (data)       => api.post('/auth/cajero-login', data),
};

// 🚗 Parking / Estacionamiento API
export const parkingAPI = {
  getStats:          (params)     => api.get('/parking/stats', { params }),
  getTarifas:        ()           => api.get('/parking/tarifas'),
  getActivos:        (params)     => api.get('/parking/activos', { params }),
  getHistorial:      (params)     => api.get('/parking/historial', { params }),
  checkIn:           (data)       => api.post('/parking/check-in', data),
  editarTicket:      (id, data)   => api.put(`/parking/${id}/editar`, data),
  calcularCheckout:  (id, params) => api.get(`/parking/calcular-checkout/${id}`, { params }),
  confirmarCheckout: (id, data)   => api.post(`/parking/confirmar-checkout/${id}`, data),
  darSalidaPrepago:  (id, data)   => api.post(`/parking/dar-salida-prepago/${id}`, data),
  anularIngreso:     (id, data)   => api.post(`/parking/anular-ingreso/${id}`, data),
  anularCobro:       (id, data)   => api.post(`/parking/anular-cobro/${id}`, data),
  // 💰 Gastos de parking (tabla separada, exclusivo parking)
  getGastos:         (params)     => api.get('/parking/gastos', { params }),
  createGasto:       (data)       => api.post('/parking/gastos', data),
  deleteGasto:       (id)         => api.delete(`/parking/gastos/${id}`),
};


// 🎁 Promos API — Tarjeta de Sellos Virtual y Promo 5+1
export const promosAPI = {
  // Tarjeta 7+1
  getTarjeta:         (customerId)           => api.get(`/promos/tarjeta/${customerId}`),
  agregarSellos:      (customerId, cantidad)  => api.post(`/promos/tarjeta/${customerId}/sellos`, { cantidad }),
  ajustarSellos:      (customerId, sellos)    => api.post(`/promos/tarjeta/${customerId}/ajustar`, { sellos }),
  canjear:            (customerId)            => api.post(`/promos/tarjeta/${customerId}/canjear`),
  deshacer:           (customerId)            => api.post(`/promos/tarjeta/${customerId}/deshacer`),
  // Promo 5+1
  promo5mas1:         (customerId, desc)      => api.post('/promos/promo5mas1', { customer_id: customerId, descripcion: desc }),
  // Resumen y lista
  getResumen:         ()                      => api.get('/promos/resumen'),
  getClientesConSellos: ()                    => api.get('/promos/clientes-con-sellos'),
};

export default api;